import { defineStore } from 'pinia';
import { ref } from 'vue';

import { onStaleDataRefetchKey, refetchKeys } from '@peanuds/app-shell';
import { useApiRequest } from '@peanuds/composables';
import { createOutgoingTransfersRequestTemplate } from '@peanuds/shared';

import { fetchPaymentsApprovalOutgoingTransfersForAccounts } from './payments-approval.fetch';
import type { PaymentsApprovalFetchOptions } from './payments-approval.request-options';

const PARTIAL_FETCH_ERROR_MESSAGE =
  'Failed to fetch payments approval count for one or more accounts.';
let hasRegisteredPaymentsApprovalCountRefreshListener = false;

export const usePaymentsApprovalCountStore = defineStore('paymentsApprovalCount', () => {
  const paymentsApprovalCount = ref(0);
  const isPaymentsApprovalCountLoading = ref(false);
  const hasLoadedPaymentsApprovalCount = ref(false);
  const paymentsApprovalCountError = ref<unknown>(undefined);
  const lastFetchRequest = ref<{
    accountIds: string[];
    options?: PaymentsApprovalFetchOptions;
  } | null>(null);
  let latestRequestId = 0;

  const {
    execute: fetchOutgoingTransfers,
    error: outgoingTransfersError,
    reset: resetOutgoingTransfers,
  } = useApiRequest(createOutgoingTransfersRequestTemplate());

  const resetPaymentsApprovalCount = () => {
    latestRequestId += 1;
    lastFetchRequest.value = null;
    resetOutgoingTransfers();
    paymentsApprovalCount.value = 0;
    isPaymentsApprovalCountLoading.value = false;
    hasLoadedPaymentsApprovalCount.value = false;
    paymentsApprovalCountError.value = undefined;
  };

  const fetchPaymentsApprovalCountForAccounts = async (
    accountIds: string[],
    options?: PaymentsApprovalFetchOptions,
  ) => {
    lastFetchRequest.value = {
      accountIds: [...accountIds],
      options,
    };

    const currentRequestId = ++latestRequestId;

    isPaymentsApprovalCountLoading.value = true;
    paymentsApprovalCount.value = 0;
    hasLoadedPaymentsApprovalCount.value = false;
    paymentsApprovalCountError.value = undefined;

    const { normalizedAccountIds, rows, hasPartialFailure, hasLoaded } =
      await fetchPaymentsApprovalOutgoingTransfersForAccounts({
        accountIds,
        fetchOutgoingTransfers,
        options,
      });

    if (normalizedAccountIds.length === 0) {
      if (currentRequestId !== latestRequestId) {
        return 0;
      }

      paymentsApprovalCount.value = 0;
      isPaymentsApprovalCountLoading.value = false;
      hasLoadedPaymentsApprovalCount.value = false;

      return 0;
    }

    if (currentRequestId !== latestRequestId) {
      return paymentsApprovalCount.value;
    }

    paymentsApprovalCount.value = rows.length;
    isPaymentsApprovalCountLoading.value = false;
    hasLoadedPaymentsApprovalCount.value = hasLoaded;
    paymentsApprovalCountError.value = hasPartialFailure
      ? (outgoingTransfersError.value ?? new Error(PARTIAL_FETCH_ERROR_MESSAGE))
      : undefined;

    return paymentsApprovalCount.value;
  };

  const fetchPaymentsApprovalCount = async (
    accountId: string,
    options?: PaymentsApprovalFetchOptions,
  ) => {
    return await fetchPaymentsApprovalCountForAccounts([accountId], options);
  };

  const refreshPaymentsApprovalCount = async () => {
    if (!hasLoadedPaymentsApprovalCount.value || lastFetchRequest.value === null) {
      return;
    }

    await fetchPaymentsApprovalCountForAccounts(
      lastFetchRequest.value.accountIds,
      lastFetchRequest.value.options,
    );
  };

  if (!hasRegisteredPaymentsApprovalCountRefreshListener) {
    hasRegisteredPaymentsApprovalCountRefreshListener = true;

    onStaleDataRefetchKey(refetchKeys.OUTGOING_TRANSFERS, () => {
      void usePaymentsApprovalCountStore().refreshPaymentsApprovalCount();
    });
  }

  return {
    paymentsApprovalCount,
    isPaymentsApprovalCountLoading,
    hasLoadedPaymentsApprovalCount,
    paymentsApprovalCountError,
    fetchPaymentsApprovalCount,
    fetchPaymentsApprovalCountForAccounts,
    refreshPaymentsApprovalCount,
    resetPaymentsApprovalCount,
  };
});
